import { useAppContext } from "../../../2_ksbHub/context/appContext";
import { useSondertagContext } from "../context/sondertagContext";
import sondertagService from "../services/sondertagService";

export default function useSondertagActions(options = {}) {
  const {
    loadSonderEventData,
    selectedSchoolyear,
    selectedSonderEvent,
    setSelectedSonderEvent,
    sonderEvents,
    sondertage,
    setSondertage,
  } = useSondertagContext();

  const { currentUser } = useAppContext();

  /**
   * =========================================================
   * ✅ SAVE ANGEBOT (CREATE + RESERVE)
   * =========================================================
   */

  const handleSaveAngebot = async ({ data, jahrgangId }) => {
    try {
      // =========================================================
      // ✅ CASE 1: UPDATE EXISTING (reservation / edit)
      // =========================================================
      if (data.id) {
        const mode = data.reserveFullWeek
          ? "week"
          : data.reserveFullDay
            ? "day"
            : "single";

        const result = await sondertagService.updateAngebot(data.id, {
          title: data.title,
          specialTitle: data.specialTitle,
          owners: data.owners,
          mode,
          joinMode: data.joinMode,
        });

        return result; // e.g. { updated: X, skipped: Y }
      }

      // =========================================================
      // ✅ CASE 2: CREATE NEW (unchanged)
      // =========================================================
      await sondertagService.createAngebot({
        title: data.title,
        specialTitle: data.specialTitle,
        owners: data.owners,
        klasseId: data.klasseId,
        halbtag: data.halbtag,
        jahrgangId,
      });

      return { created: 1, skipped: [] };
    } catch (err) {
      console.error("handleSaveAngebot failed", err);
      throw err;
    }
  };

  const handleDeleteAngebot = async (angebot) => {
    if (!angebot?.id) return;

    const confirmDelete = window.confirm("Wirklich löschen?");
    if (!confirmDelete) return;

    try {
      await sondertagService.deleteAngebot(angebot.id);

      // ✅ no reload here — socket handles it
    } catch (err) {
      console.error("❌ Delete error:", err);
    }
  };

  const handleLockAngebot = async (angebotId) => {
    try {
      await sondertagService.lockAngebot(
        angebotId,
        currentUser.id,
        currentUser.abbreviation,
      );
    } catch (err) {
      console.error("Lock failed", err);
    }
  };

  const handleUnlockAngebot = async (angebotId) => {
    try {
      await sondertagService.unlockAngebot(angebotId);
    } catch (err) {
      console.error("Unlock failed", err);
    }
  };

  /**
   * =========================================================
   * ✅ CREATE SONDER EVENT
   * =========================================================
   * (no optimistic → structure reload better)
   */
  const handleSaveSonderEvent = async (eventData, editingSondertag) => {
    return editingSondertag
      ? updateSonderEvent(eventData) // ✅ update
      : handleCreateSonderEvent(eventData); // ✅ create
  };

  const handleCreateSonderEvent = async (eventData) => {
    if (!selectedSchoolyear?.id) return;
    //backend handles template cases
    await sondertagService.createSonderEvent({
      ...eventData,
      schoolyearId: eventData.schoolyearId
        ? eventData.schoolyearId
        : selectedSchoolyear.id,
      role: eventData.role,
    });
  };

  const updateSonderEvent = async (sondertag) => {
    return await sondertagService.updateSonderEvent(sondertag.id, sondertag);
  };

  const handleArchiveSonderEvent = async (eventId) => {
    await sondertagService.archiveSondertag(eventId);

    // await loadSonderEventData(selectedSonderEvent.id);
  };

  const handleDeleteSonderEvent = async (eventId) => {
    await sondertagService.deleteSonderEvent(eventId);

    // ✅ reload everything
    await loadSonderEventData(selectedSchoolyear.id);

    // ✅ reset selected sondertag if needed
    setSelectedSonderEvent(sonderEvents?.length > 0 ? sonderEvents[0] : null);
  };

  /**
   * =========================================================
   * ✅ ADD SONDERTAG (OPTIMISTIC ✅)
   * =========================================================
   */

  const handleSaveSondertag = async (sondertag, editingSondertag) => {
    // ✅ EDIT
    if (editingSondertag) {
      return updateSondertag(sondertag);
    }

    // ✅ TEMPLATE
    if (sondertag.templateId) {
      return handleAddSondertagFromTemplate(sondertag);
    }

    // ✅ NORMAL CREATE
    return addSondertag(sondertag);
  };

  const addSondertag = async (sondertag) => {
    if (!selectedSonderEvent?.id) return;

    try {
      await sondertagService.createSondertag({
        ...sondertag,
        eventId: selectedSonderEvent.id,
      });

      // ✅ reload AFTER creation
      await loadSonderEventData(selectedSonderEvent.id);
    } catch (err) {
      // console.error("Create sondertag failed", err);
      throw err; // ✅ Error will be shown in dialog
    }
  };

  const handleAddSondertagFromTemplate = async (sondertag) => {
    if (!selectedSonderEvent?.id) return;

    try {
      await sondertagService.createSondertagFromTemplate({
        ...sondertag,
        eventId: selectedSonderEvent.id,
      });

      // ✅ reload AFTER creation
      await loadSonderEventData(selectedSonderEvent.id);
    } catch (err) {
      // console.error("Create sondertag failed", err);
      throw err; // ✅ Error will be shown in dialog
    }
  };

  const updateSondertag = async (sondertag) => {
    if (!selectedSonderEvent?.id) return;

    try {
      const result = await sondertagService.updateSondertag(sondertag.id, {
        ...sondertag,
        eventId: selectedSonderEvent.id,
      });

      await loadSonderEventData(selectedSonderEvent.id);

      return result;
    } catch (err) {
      // console.error("Update sondertag failed", err);
      throw err; // ✅ Error will be shown in dialog
    }
  };
  /**
   * =========================================================
   * ✅ DELETE SHIFT (OPTIMISTIC ✅)
   * =========================================================
   */
  const handleDeleteSondertag = async (sondertagId) => {
    const old = sondertage;

    // ✅ optimistic
    setSondertage((prev) => prev.filter((s) => s.id !== sondertagId));

    try {
      await sondertagService.deleteSondertag(sondertagId);
    } catch (err) {
      console.error("Delete sondertag failed → rollback", err);
      setSondertage(old); // rollback
    }
  };

  /**
   * =========================================================
   * ✅ JOBS (safe → keep API-first)
   * =========================================================
   */
  const addJahrgang = async (job) => {
    const res = await sondertagService.createJahrgang(job);

    // ✅ reload full structure
    await loadSonderEventData(selectedSonderEvent.id);

    return res;
  };

  const updateJahrgang = async (job) => {
    await sondertagService.updateJahrgang(job.id, job);

    await loadSonderEventData(selectedSonderEvent.id);
  };

  const handleSaveJahrgang = async (jahrgang, editingJahrgang) => {
    return editingJahrgang ? updateJahrgang(jahrgang) : addJahrgang(jahrgang);
  };

  const handleDeleteJob = async (jahrgangId) => {
    await sondertagService.deleteJahrgang(jahrgangId);

    await loadSonderEventData(selectedSonderEvent.id);
  };

  /**
   * =========================================================
   * ✅ LOCK (OPTIMISTIC ✅)
   * =========================================================
   */
  const handleToggleLock = async ({
    unlockTime,
    lockForStudents,
    lockForTeachers,
  }) => {
    if (!selectedSonderEvent?.id) return;

    const prevState = {
      unlockTime: selectedSonderEvent.unlockTime,
      lockForStudents: selectedSonderEvent.lockForStudents,
      lockForTeachers: selectedSonderEvent.lockForTeachers,
    };

    // ✅ optimistic update
    setSelectedSonderEvent((prev) => ({
      ...prev,
      unlockTime,
      lockForStudents,
      lockForTeachers,
    }));

    try {
      await sondertagService.updateLock(selectedSonderEvent.id, {
        unlockTime,
        lockForStudents,
        lockForTeachers,
      });
    } catch (err) {
      console.error("Lock update failed → rollback", err);

      // ❌ rollback
      setSelectedSonderEvent((prev) => ({
        ...prev,
        ...prevState,
      }));
    }
  };

  /**
   * =========================================================
   * ✅ UNLOCK NOW (OPTIMISTIC ✅)
   * =========================================================
   */
  const handleUnlockNow = async () => {
    if (!selectedSonderEvent?.id) return;

    const prevState = {
      unlockTime: selectedSonderEvent.unlockTime,
      lockForStudents: selectedSonderEvent.lockForStudents,
      lockForTeachers: selectedSonderEvent.lockForTeachers,
    };

    // ✅ optimistic update → clear everything
    setSelectedSonderEvent((prev) => ({
      ...prev,
      unlockTime: null,
      lockForStudents: false,
      lockForTeachers: false,
    }));

    try {
      await sondertagService.updateLock(selectedSonderEvent.id, {
        unlockTime: null,
        lockForStudents: false,
        lockForTeachers: false,
      });
    } catch (err) {
      console.error("Unlock failed → rollback", err);

      setSelectedSonderEvent((prev) => ({
        ...prev,
        ...prevState,
      }));
    }
  };

  /**
   * =========================================================
   * ✅ RETURN API
   * =========================================================
   */
  return {
    handleCreateSonderEvent,
    handleSaveSonderEvent,
    handleArchiveSonderEvent,
    handleDeleteSonderEvent,
    handleSaveJahrgang,
    handleDeleteJob,
    handleSaveSondertag,
    handleDeleteSondertag,
    handleToggleLock,
    handleSaveAngebot,
    handleUnlockNow,
    handleLockAngebot,
    handleUnlockAngebot,
    handleDeleteAngebot,
  };
}
